/**
 * Header fixo da aplicação
 */

import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { ExitToApp } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@contexts';
import { FormatUtil, SessionUtil } from '@utils';

interface HeaderProps {
  title?: string;
  showUserInfo?: boolean;
  onLogout?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  title = 'Pedidos Avulsos',
  showUserInfo = true,
  onLogout,
}) => {
  const { vendedor, logout } = useAuthContext();
  const navigate = useNavigate();
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  // Carrega dados do vendedor direto do localStorage para garantir atualização
  const getVendedorData = () => {
    const sessionData = SessionUtil.getVendedor();
    if (!sessionData) return null;
    return {
      nome: sessionData.vendNome,
    };
  };

  const [vendedorData, setVendedorData] = React.useState(getVendedorData());

  // Atualiza dados do vendedor periodicamente
  React.useEffect(() => {
    const interval = setInterval(() => {
      const newVendedorData = getVendedorData();
      setVendedorData(newVendedorData);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Atualiza dados quando vendedor do contexto mudar
  React.useEffect(() => {
    const newVendedorData = getVendedorData();
    setVendedorData(newVendedorData);
  }, [vendedor]);

  const checkPrevisaoNaoEnviada = (): boolean => {
    try {
      // Verifica se tem carrinho de previsão não vazio
      const carrinhoData = localStorage.getItem('carrinho');
      const carrinhoTipo = localStorage.getItem('carrinhoTipo'); // Chave correta: camelCase
      const indPrevisaoEnvio = localStorage.getItem('indPrevisaoEnvio');

      console.log('[Header] Verificando previsão não enviada:', {
        carrinhoData: carrinhoData ? 'existe' : 'null',
        carrinhoTipo,
        indPrevisaoEnvio,
      });

      if (carrinhoData && carrinhoTipo === 'previsao') {
        const carrinho = JSON.parse(carrinhoData);
        const temItens = carrinho.quantidadeTotal > 0;
        const naoEnviado = indPrevisaoEnvio !== '1';

        console.log('[Header] Carrinho de previsão encontrado:', {
          quantidadeTotal: carrinho.quantidadeTotal,
          temItens,
          naoEnviado,
          resultado: temItens && naoEnviado,
        });

        return temItens && naoEnviado;
      }

      console.log('[Header] Não é carrinho de previsão ou carrinho vazio');
      return false;
    } catch (error) {
      console.error('Erro ao verificar previsão não enviada:', error);
      return false;
    }
  };

  const handleLogout = () => {
    // Verifica se há previsão não enviada
    if (checkPrevisaoNaoEnviada()) {
      setWarningDialogOpen(true);
    } else {
      performLogout();
    }
  };

  const performLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      logout();
      // Redireciona para login após logout
      navigate('/login', { replace: true });
    }
  };

  const handleCancelLogout = () => {
    setWarningDialogOpen(false);
  };

  const handleConfirmLogout = () => {
    setWarningDialogOpen(false);
    performLogout();
  };

  return (
    <>
      <AppBar position="fixed" color="primary" sx={{borderRadius:0, zIndex:1}} elevation={0}>
        <Toolbar>
          {showUserInfo && vendedorData && (
            <IconButton
              edge="start"
              color="inherit"
              sx={{ mr: 2 }}
              disabled
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
                {FormatUtil.getInitials(vendedorData.nome)}
              </Avatar>
            </IconButton>
          )}

          <Typography variant="h6" component="div" sx={{ flexGrow: 1, textAlign: 'center' }}>
            {title}
          </Typography>

          {showUserInfo && vendedorData && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleLogout}
              aria-label="sair"
            >
              <ExitToApp />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Modal de aviso - Previsão não enviada */}
      <Dialog open={warningDialogOpen} onClose={handleCancelLogout} maxWidth="sm" fullWidth>
        <DialogTitle>Atenção!</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Você possui itens no carrinho de previsão que ainda não foram enviados.
          </Typography>
          <Typography color="error" fontWeight="bold">
            Se sair agora, os dados não salvos serão perdidos.
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Deseja realmente sair sem enviar a previsão?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout} variant="contained" color="primary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmLogout} variant="outlined" color="error">
            Sair Mesmo Assim
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;